import React, { useState, useRef, useEffect, useCallback } from 'react';
import './PhotoBooth.css';
import cameraSound from '../sounds/camera.mp3';

interface CapturedPhoto {
  id: string;
  dataUrl: string;
  timestamp: number;
  filename: string;
}

interface Filter {
  name: string;
  cssFilter: string;
}

interface PhotoBoothProps {
  onOpenPreview?: (photoUrl: string, photoId: string) => void;
}

const PhotoBooth: React.FC<PhotoBoothProps> = ({ onOpenPreview }) => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentFilter, setCurrentFilter] = useState<string>('none');
  const [showEffects, setShowEffects] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [capturedPhotos, setCapturedPhotos] = useState<CapturedPhoto[]>([]);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [showFlash, setShowFlash] = useState(false);
  const [triggerWiggle, setTriggerWiggle] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const effectsCanvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const filters: Filter[] = [
    { name: 'Normal', cssFilter: 'none' },
    { name: 'Black & White', cssFilter: 'grayscale(100%)' },
    { name: 'Sepia', cssFilter: 'sepia(100%)' },
    { name: 'Vintage', cssFilter: 'sepia(50%) contrast(120%) brightness(110%)' },
    { name: 'Cool', cssFilter: 'hue-rotate(90deg) saturate(150%)' },
    { name: 'Warm', cssFilter: 'hue-rotate(-30deg) saturate(120%) brightness(110%)' },
    { name: 'High Contrast', cssFilter: 'contrast(200%) saturate(150%)' },
    { name: 'X-Ray', cssFilter: 'invert(100%) contrast(200%)' },
    { name: 'Bright', cssFilter: 'brightness(150%) saturate(120%)' }
  ];

  useEffect(() => {
    // Initialize audio
    audioRef.current = new Audio(cameraSound);

    startCamera();
    loadSavedPhotos();

    // Add window beforeunload handler to ensure camera is stopped when window closes
    const handleBeforeUnload = () => {
      console.log('📹 Window closing - stopping camera');
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      // Clean up camera stream on unmount
      console.log('📹 PhotoBooth unmounting - cleaning up camera');

      // Remove beforeunload handler
      window.removeEventListener('beforeunload', handleBeforeUnload);

      // Stop all tracks FIRST, before clearing references
      if (streamRef.current) {
        console.log('📹 Stopping camera stream, tracks:', streamRef.current.getTracks().length);
        streamRef.current.getTracks().forEach(track => {
          console.log('📹 Stopping track:', track.kind, 'enabled:', track.enabled, 'readyState:', track.readyState);
          track.stop();
          console.log('📹 Track stopped, new readyState:', track.readyState);
        });
      }

      // Then clear all video element references
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
        videoRef.current.load(); // Force release
        console.log('📹 Cleared main video srcObject');
      }

      // Finally clear state
      if (streamRef.current) {
        streamRef.current = null;
        setStream(null);
      }

      console.log('📹 PhotoBooth cleanup complete');
    };
  }, []);

  // Ensure main video always has the stream when not showing effects
  useEffect(() => {
    if (!showEffects && videoRef.current && stream && !videoRef.current.srcObject) {
      console.log('📹 Re-establishing main video stream');
      videoRef.current.srcObject = stream;
    }
  }, [showEffects, stream]);

  const loadSavedPhotos = () => {
    try {
      const saved = localStorage.getItem('photoboothPhotos');
      if (saved) {
        setCapturedPhotos(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading saved photos:', error);
    }
  };

  const savePhotosToStorage = useCallback((photos: CapturedPhoto[]) => {
    try {
      localStorage.setItem('photoboothPhotos', JSON.stringify(photos));
    } catch (error) {
      console.error('Error saving photos:', error);
    }
  }, []);

  const startCamera = async () => {
    console.log('📹 Starting camera...');

    // Prevent creating multiple streams
    const currentStream = streamRef.current;
    if (currentStream) {
      const isActive = currentStream.getTracks().some(track => track.readyState === 'live');
      if (isActive) {
        console.log('📹 Camera already started and active, skipping');
        return;
      }
      // If there's an inactive stream, clean it up first
      console.log('📹 Cleaning up inactive stream before creating new one');
      currentStream.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: 640,
          height: 480,
          aspectRatio: 4/3
        }
      });

      console.log('📹 Camera stream obtained:', mediaStream);

      // Double-check stream wasn't created while we were waiting
      const streamAfterWait = streamRef.current;
      if (streamAfterWait) {
        const isActive = streamAfterWait.getTracks().some(track => track.readyState === 'live');
        if (isActive) {
          console.log('📹 Stream was created while waiting, stopping new stream');
          mediaStream.getTracks().forEach(track => track.stop());
          return;
        }
      }

      streamRef.current = mediaStream; // Store in ref for cleanup
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        setIsStreaming(true);
        console.log('📹 Video element configured, streaming:', true);
      }
    } catch (error) {
      console.error('❌ Error accessing camera:', error);
    }
  };

  const capturePhoto = async () => {
    console.log('📸 Attempting to capture photo...');
    console.log('📸 Video ref:', videoRef.current);
    console.log('📸 Canvas ref:', canvasRef.current);
    console.log('📸 Current filter:', currentFilter);
    
    if (!videoRef.current || !canvasRef.current) {
      console.error('❌ Missing video or canvas ref');
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    if (!context) {
      console.error('❌ Could not get canvas context');
      return;
    }

    console.log('📸 Video dimensions:', video.videoWidth, 'x', video.videoHeight);
    console.log('📸 Video ready state:', video.readyState);

    // Calculate dimensions for 4:3 aspect ratio
    const videoWidth = video.videoWidth || 640;
    const videoHeight = video.videoHeight || 480;
    const targetAspect = 4 / 3;
    const videoAspect = videoWidth / videoHeight;

    let sourceWidth, sourceHeight, sourceX, sourceY;

    if (videoAspect > targetAspect) {
      // Video is wider than 4:3, crop sides
      sourceHeight = videoHeight;
      sourceWidth = videoHeight * targetAspect;
      sourceX = (videoWidth - sourceWidth) / 2;
      sourceY = 0;
    } else {
      // Video is taller than 4:3, crop top/bottom (center crop)
      sourceWidth = videoWidth;
      sourceHeight = videoWidth / targetAspect;
      sourceX = 0;
      sourceY = (videoHeight - sourceHeight) / 2;
    }

    // Set canvas to 4:3 aspect ratio at reasonable size
    const canvasWidth = 640;
    const canvasHeight = 480;
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    // Apply filter to canvas
    if (currentFilter !== 'none') {
      context.filter = currentFilter;
      console.log('📸 Applied filter:', currentFilter);
    }

    // Flip horizontally for mirror effect and draw cropped image
    context.scale(-1, 1);
    context.drawImage(
      video,
      sourceX, sourceY, sourceWidth, sourceHeight,  // Source rectangle (cropped)
      -canvasWidth, 0, canvasWidth, canvasHeight    // Destination rectangle
    );
    context.setTransform(1, 0, 0, 1, 0, 0); // Reset transform
    
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    console.log('📸 Generated dataUrl length:', dataUrl.length);
    
    const timestamp = Date.now();
    const photo: CapturedPhoto = {
      id: timestamp.toString(),
      dataUrl,
      timestamp,
      filename: `PhotoBooth_${timestamp}.jpg`
    };

    const updatedPhotos = [photo, ...capturedPhotos];
    setCapturedPhotos(updatedPhotos);
    savePhotosToStorage(updatedPhotos);
    console.log('📸 Photo captured and saved! Total photos:', updatedPhotos.length);
  };


  const startCountdown = () => {
    console.log('⏱️ Starting countdown...');

    // Play camera sound immediately when button is clicked (contains all beeps + shutter)
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(err => console.error('Error playing sound:', err));
    }

    // Show "3" immediately
    setCountdown(3);

    // After 1 second, show "2"
    setTimeout(() => setCountdown(2), 1000);

    // After 2 seconds, show "1"
    setTimeout(() => setCountdown(1), 2000);

    // After 3 seconds: flash + capture photo + hide countdown
    setTimeout(() => {
      // Show flash and capture at the same time (3000ms - synced with camera shutter sound)
      setShowFlash(true);
      capturePhoto();
      setCountdown(null);

      // Hide flash after 300ms
      setTimeout(() => {
        setShowFlash(false);
      }, 300);

      // Start wiggle 1 second after photo is taken (at 4000ms total)
      setTimeout(() => {
        setTriggerWiggle(true);

        // Reset wiggle after animation completes
        setTimeout(() => {
          setTriggerWiggle(false);
        }, 500);
      }, 1000);
    }, 3000);
  };

  const selectFilter = (filter: string) => {
    console.log('🎨 Selecting filter:', filter);
    setCurrentFilter(filter);
    setShowEffects(false);
    
    // Ensure main video keeps its stream when returning from effects view
    if (videoRef.current && stream) {
      console.log('🎨 Re-attaching stream to main video');
      videoRef.current.srcObject = stream;
    }
    console.log('🎨 Filter applied, returning to main view');
  };

  return (
    <div className="photobooth">
      <div className="photobooth-content">
        {/* Main Camera View */}
        {!showEffects && (
          <div className="camera-view">
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              onLoadedMetadata={() => console.log('📹 Main video loaded, dimensions:', videoRef.current?.videoWidth, 'x', videoRef.current?.videoHeight)}
              onError={(e) => console.error('❌ Main video error:', e)}
              style={{
                filter: currentFilter,
                transform: 'scaleX(-1)', // Mirror effect like real photobooth
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
            {/* White flash overlay */}
            {showFlash && <div className="camera-flash" />}
            
            {/* Countdown Overlay */}
            {countdown && (
              <div className="countdown-overlay">
                <div className="countdown-number">{countdown}</div>
              </div>
            )}
          </div>
        )}

        {/* Effects Grid */}
        {showEffects && (
          <div className="effects-grid">
            {filters.map((filter, index) => (
              <div 
                key={index}
                className="effect-item"
                onClick={() => selectFilter(filter.cssFilter)}
              >
                <div 
                  className="effect-preview"
                  style={{ 
                    filter: filter.cssFilter,
                    transform: 'scaleX(-1)'
                  }}
                >
                  <EffectPreview stream={stream} filter={filter.cssFilter} />
                </div>
                <div className="effect-label">{filter.name}</div>
              </div>
            ))}
          </div>
        )}

      </div>

      {/* Photo Gallery Drawer */}
      <div className={`photo-gallery-drawer ${showGallery ? 'open' : ''}`}>
        <div className="gallery-content">
          {capturedPhotos.length === 0 ? (
            <div className="no-photos">No photos taken yet</div>
          ) : (
            <div className="photos-scroll">
              {capturedPhotos.map(photo => (
                <div
                  key={photo.id}
                  className="photo-thumbnail"
                  onClick={() => onOpenPreview?.(photo.dataUrl, photo.filename)}
                >
                  <img src={photo.dataUrl} alt={`Captured ${photo.id}`} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Menu */}
      <div className="photobooth-menu">
        <button
          className={`menu-button photos-button ${triggerWiggle ? 'wiggle' : ''}`}
          onClick={() => {
            console.log('📁 Gallery button clicked, current state:', showGallery);
            setShowGallery(!showGallery);
            setShowEffects(false);
          }}
        >
          📁
        </button>

        <button 
          className="menu-button capture-button"
          onClick={startCountdown}
          disabled={countdown !== null}
        >
          <div className="capture-circle" />
        </button>

        <button 
          className="menu-button effects-button"
          onClick={() => {
            console.log('🎨 Effects button clicked, current state:', showEffects);
            setShowEffects(!showEffects);
            setShowGallery(false);
          }}
        >
          ✨
        </button>
      </div>

      {/* Hidden canvas for photo capture */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      <canvas ref={effectsCanvasRef} style={{ display: 'none' }} />
    </div>
  );
};

// Component for showing live filter previews
interface EffectPreviewProps {
  stream: MediaStream | null;
  filter: string;
}

const EffectPreview: React.FC<EffectPreviewProps> = ({ stream, filter }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    console.log('🎨 Setting up effect preview for filter:', filter);
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
      console.log('🎨 Stream attached to preview video');
    } else {
      console.log('🎨 Missing video ref or stream for preview');
    }

    // Cleanup when preview unmounts
    return () => {
      if (videoRef.current) {
        videoRef.current.srcObject = null;
        console.log('🎨 Cleared preview video srcObject for filter:', filter);
      }
    };
  }, [stream, filter]);

  return (
    <video
      ref={videoRef}
      autoPlay
      muted
      playsInline
      onLoadedMetadata={() => console.log('🎨 Preview video loaded for filter:', filter)}
      onError={(e) => console.error('❌ Preview video error for filter:', filter, e)}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        filter: filter
      }}
    />
  );
};

export default PhotoBooth;